import { useState } from 'react';
import { Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useI18n } from '@/contexts/I18nContext';
import type { ExceptionLog } from '@/types/exception.types';

interface ExceptionLogDetailProps {
  log: ExceptionLog | null;
  open: boolean;
  onClose: () => void;
}

function severityClass(severity: string) {
  switch (severity) {
    case 'info': return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800';
    case 'warn': return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800';
    case 'error': return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800';
    case 'critical': return 'bg-red-100 text-red-900 border-red-400 dark:bg-red-900 dark:text-red-100 dark:border-red-700 font-bold';
    default: return '';
  }
}

function CollapsibleJson({ title, data }: { title: string; data: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between h-8 px-2 text-xs">
          {title}
          {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="bg-muted rounded p-3 text-xs overflow-auto max-h-48 mt-1">
          {JSON.stringify(data, null, 2)}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ExceptionLogDetail({ log, open, onClose }: ExceptionLogDetailProps) {
  const { tp } = useI18n();
  const [stackOpen, setStackOpen] = useState(false);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => toast.success(label));
  };

  if (!log) return null;

  const sqlQuery = `SELECT * FROM exception_logs WHERE id = '${log.id}';`;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full max-w-2xl p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b shrink-0">
          <SheetTitle className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={severityClass(log.severity)}>
              {log.error_code}
            </Badge>
            <Badge variant="outline" className={severityClass(log.severity)}>
              {log.severity}
            </Badge>
            {log.correlation_id && (
              <span className="flex items-center gap-1 text-xs bg-muted rounded px-2 py-0.5 font-mono">
                {log.correlation_id.slice(0, 16)}…
                <Button
                  variant="ghost" size="icon" className="h-4 w-4"
                  onClick={() => copyToClipboard(log.correlation_id!, tp.exceptions.detail.correlationIdCopied)}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div>
            <p className="text-sm font-semibold text-muted-foreground mb-1">{tp.exceptions.detail.message}</p>
            <p className="text-base">{log.message}</p>
          </div>

          {(log.origin_file || log.origin_function) && (
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-1">{tp.exceptions.detail.origin}</p>
              {log.origin_file && (
                <code className="text-xs bg-muted rounded px-2 py-1 block">
                  {log.origin_file}{log.origin_line != null ? `:${log.origin_line}` : ''}
                </code>
              )}
              {log.origin_function && (
                <p className="text-xs text-muted-foreground mt-1">{tp.exceptions.detail.function}: {log.origin_function}</p>
              )}
            </div>
          )}

          {(log.request_method || log.request_url || log.user_id || log.tenant_id || log.ip_address) && (
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-1">{tp.exceptions.detail.request}</p>
              <div className="space-y-1 text-xs">
                {log.request_method && log.request_url && (
                  <div className="font-mono bg-muted rounded px-2 py-1">{log.request_method} {log.request_url}</div>
                )}
                {log.user_id && <div><span className="text-muted-foreground">{tp.exceptions.detail.userId}: </span>{log.user_id}</div>}
                {log.tenant_id && <div><span className="text-muted-foreground">{tp.exceptions.detail.tenantId}: </span>{log.tenant_id}</div>}
                {log.ip_address && <div><span className="text-muted-foreground">{tp.exceptions.detail.ipAddress}: </span>{log.ip_address}</div>}
              </div>
            </div>
          )}

          {log.details != null && (
            <div>
              <p className="text-sm font-semibold text-muted-foreground mb-1">{tp.exceptions.detail.details}</p>
              <pre className="bg-muted rounded p-3 text-xs overflow-auto max-h-48">
                {JSON.stringify(log.details, null, 2)}
              </pre>
            </div>
          )}

          {log.stack && (
            <div>
              <Collapsible open={stackOpen} onOpenChange={setStackOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between h-8 px-2 text-xs font-semibold">
                    {tp.exceptions.detail.stackTrace}
                    {stackOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <pre className="text-xs font-mono whitespace-pre-wrap bg-muted rounded p-3 mt-1 overflow-auto max-h-64">
                    {log.stack}
                  </pre>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {log.request_body && <CollapsibleJson title={tp.exceptions.detail.requestBody} data={log.request_body} />}
          {log.request_query && <CollapsibleJson title={tp.exceptions.detail.requestQuery} data={log.request_query} />}
          {log.request_headers && <CollapsibleJson title={tp.exceptions.detail.requestHeaders} data={log.request_headers} />}

          <div>
            <p className="text-sm font-semibold text-muted-foreground mb-1">{tp.exceptions.detail.sqlHelper}</p>
            <div className="flex items-center gap-2 bg-muted rounded px-3 py-2">
              <code className="text-xs font-mono flex-1 text-muted-foreground break-all">{sqlQuery}</code>
              <Button
                variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                onClick={() => copyToClipboard(sqlQuery, tp.exceptions.detail.sqlCopied)}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
